/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import { Skeleton, Tooltip } from "antd";
import clsx from "clsx";
import { ethers } from "ethers";
import Web3 from "web3";
import { AbiItem } from "web3-utils";
import { useAccount } from "wagmi";
import { useWeb3ModalState } from "@web3modal/wagmi/react";

import Action from "../action";
import abiStake from "../utils/abiStake.json";
import abiTokenStake from "../utils/abiTokenStake.json";
import { STAKING_TOKEN_ABI } from "../../../../../abi/abi-staking-token";
import { STAKING_TOKEN_COMPOUND_ABI } from "../../../../../abi/abi-staking-token-compound";
import { formatNumber } from "../utils";
import { useContract } from "../../../../../hooks/useContract";
import { useHookStaked } from "../../storeStaked";
import { Noti } from "../../../../../components/Noti/toast";
import LoadingPage from "../../../../../components/loadingPage";
import {
  NUM_FORMAT,
  RPC_BASE,
  STAKING_PORTFOLIO_URL,
  WORK_CHAIN,
} from "../../../../../constants";
import {
  V2Pool,
  mapStakingTokenToV2,
  isPoolActiveByEndTime,
  readPoolTvl,
} from "./v2Pools";
import { RESTAKE_STEPS, useRestakeFlow } from "./useRestakeFlow";
import { getStakingTokenTypeLabel } from "../../../../../constants/stakingToken";

const web3: any = new Web3(RPC_BASE);

const PROGRESS_LABELS = [
  "Unstake",
  "Withdraw to restake",
  "V2 Restaking Complete",
];

/** Tooltip copy for the pool-card stat info icons. */
const TIP = {
  totalStaked:
    "The total amount of tokens deposited into this staking pool by all users.",
  apr: "Annual Percentage Rate for this staking pool.",
  earlyFee: "Fee charged for withdrawing tokens before the lock period ends.",
  lockDuration: "Tokens are locked and cannot be unstaked during this period.",
};

/** Info icon (Figma node 25664:58402) with a hover tooltip. */
const InfoIc = ({ tip }: { tip: string }) => (
  <Tooltip title={tip}>
    <img className="rv2-info-ic" src="/images/staking/icon-info.svg" alt="" />
  </Tooltip>
);

const RestakeProgress = ({ step }: { step: number }) => (
  <div className="rv2-progress">
    {PROGRESS_LABELS.map((label, i) => (
      <React.Fragment key={label}>
        <div
          className={`rv2-progress-node ${i <= step ? "active" : ""} ${
            i < step ? "done" : ""
          }`}
        >
          <span className="rv2-progress-dot" />
          <span className="rv2-progress-label">{label}</span>
        </div>
        {i < PROGRESS_LABELS.length - 1 && (
          <span className={`rv2-progress-line ${i < step ? "active" : ""}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

/** Single selectable V2 pool card (also reused read-only on the Withdraw step). */
const V2PoolCard = ({
  pool,
  selected,
  onSelect,
  readOnly,
}: {
  pool: V2Pool;
  selected?: boolean;
  onSelect?: () => void;
  readOnly?: boolean;
}) => (
  <div
    className={`rv2-pool-card ${pool.autoCompound ? "auto" : ""} ${
      selected ? "selected" : ""
    } ${readOnly ? "readonly" : ""}`}
    onClick={readOnly ? undefined : onSelect}
  >
    <div className="rv2-pool-card-left">
      <div className="rv2-pool-logo">
        <img
          src={
            pool.autoCompound
              ? "/images/staking/v2-pool-logo-auto.png"
              : "/images/staking/v2-pool-logo.svg"
          }
          alt=""
        />
      </div>
      <div className="rv2-pool-meta">
        <div className="rv2-pool-title-group">
          {pool.autoCompound && (
            <div className="rv2-pool-badge">AUTO COMPOUND</div>
          )}
          <div className="rv2-pool-name">{pool.poolName}</div>
        </div>
        <div
          className={clsx(
            "rv2-pool-lock",
            getStakingTokenTypeLabel(pool.type)?.labelClassName ||
              "bg-[rgba(231,234,238,0.2)]",
          )}
        >
          <img src="/images/staking/icon-day.png" alt="" />{" "}
          {getStakingTokenTypeLabel(pool.type)?.label ||
            `${pool.lockMonths} months`}
        </div>
      </div>
    </div>
    <div className="rv2-pool-stats">
      <div className="rv2-pool-stat-col">
        <div className="rv2-pool-stat">
          <span className="lb">
            {pool.autoCompound ? "APY" : "APR"}{" "}
            <InfoIc
              tip={
                pool.autoCompound
                  ? `Estimated APY based on daily compounding, up to ${formatNumber(
                      pool.apr,
                      NUM_FORMAT,
                    )}%.`
                  : TIP.apr
              }
            />
          </span>
          <span className="vl">{formatNumber(pool.apr, NUM_FORMAT)}%</span>
        </div>
        <div className="rv2-pool-stat">
          <span className="lb">
            Early withdrawal fee <InfoIc tip={TIP.earlyFee} />
          </span>
          <span className="vl">{formatNumber(pool.earlyFee, NUM_FORMAT)}%</span>
        </div>
      </div>
      <div className="rv2-pool-stat-col">
        <div className="rv2-pool-stat">
          <span className="lb">
            Total Staked <InfoIc tip={TIP.totalStaked} />
          </span>
          <span className="vl">
            {formatNumber(pool.tvl, NUM_FORMAT)}{" "}
            <em className="unit">${pool.symbol}</em>
          </span>
        </div>
        {pool.autoCompound && (
          <div className="rv2-pool-stat">
            <span className="lb">
              Lock Duration <InfoIc tip={TIP.lockDuration} />
            </span>
            <span className="vl">{`${pool.lockMonths} Months`}</span>
          </div>
        )}
      </div>
    </div>
    {!readOnly && <span className={`rv2-radio ${selected ? "checked" : ""}`} />}
  </div>
);

interface RestakeV2Props {
  poolContract: string;
  tokenContract: string;
  symbol?: string;
  endTime?: boolean;
  /** 0 to start fresh, RESTAKE_STEPS.WITHDRAW (2) when resuming after signature 1 */
  initialStep?: number;
  onExit?: () => void;
  setIsCheckAction?: (v: any) => void;
  isCheckAction?: boolean;
}

const RestakeV2: React.FC<RestakeV2Props> = ({
  poolContract,
  tokenContract,
  symbol = "MEY",
  endTime,
  initialStep = RESTAKE_STEPS.UNSTAKE,
  onExit,
  setIsCheckAction,
  isCheckAction,
}) => {
  const { address } = useAccount();
  const { selectedNetworkId }: any = useWeb3ModalState();
  const [, actions] = useHookStaked();

  const {
    flowStep,
    setFlowStep,
    selectedPoolId,
    setSelectedPoolId,
    restakeAmount,
    setRestakeAmount,
    txStatus,
    setTxStatus,
    reset,
  } = useRestakeFlow({ v1Pool: poolContract, address, initialStep });

  const [v2Pools, setV2Pools] = useState<V2Pool[]>([]);
  const [v2PoolsLoading, setV2PoolsLoading] = useState(true);
  const selectedV2Pool = v2Pools.find((p) => p.poolId === selectedPoolId);

  // Write contracts (ethers + signer).
  const v1Contract: any = useContract(poolContract, abiStake);
  // V2 stake/withdraw uses the staking-token ABI (compound vs fixed per pool).
  const v2Abi = selectedV2Pool?.autoCompound
    ? STAKING_TOKEN_COMPOUND_ABI
    : STAKING_TOKEN_ABI;
  const v2Contract: any = useContract(selectedV2Pool?.poolContract, v2Abi);
  const v2Token: any = useContract(
    selectedV2Pool?.tokenContract,
    abiTokenStake,
  );

  const [rawStakeBalance, setRawStakeBalance] = useState("0"); // wei
  const [stakeBalance, setStakeBalance] = useState(0); // token units
  const [rawPending, setRawPending] = useState("0"); // wei (inUnStakeAmount)
  const [refresh, setRefresh] = useState(0);
  // label shown in the loading overlay for the current on-chain step
  const [txLabel, setTxLabel] = useState("");

  const busy =
    txStatus === "signing1" ||
    txStatus === "pending1" ||
    txStatus === "signing2" ||
    txStatus === "pending2";

  // ---- on-chain reads on the V1 pool (RPC, no signer needed) ----
  useEffect(() => {
    if (!poolContract || !address) {
      setRawStakeBalance("0");
      setStakeBalance(0);
      setRawPending("0");
      return;
    }
    const read = new web3.eth.Contract(
      abiStake as unknown as AbiItem,
      poolContract,
    );
    read.methods
      .balanceOf(address)
      .call()
      .then((res: any) => {
        setRawStakeBalance(res?.toString() || "0");
        setStakeBalance(Number(web3.utils.fromWei(res || "0", "ether")));
      })
      .catch(() => {
        setRawStakeBalance("0");
        setStakeBalance(0);
      });
    read.methods
      .userMap(address)
      .call()
      .then((user: any) => {
        const pending = user?.inUnStakeAmount?.toString() || "0";
        setRawPending(pending);
        if (Number(pending) > 0) {
          setRestakeAmount(web3.utils.fromWei(pending, "ether"));
        }
      })
      .catch(() => setRawPending("0"));
  }, [poolContract, address, selectedNetworkId, refresh]);

  // ---- fetch V2 pools (only those that have NOT reached endTime) ----
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setV2PoolsLoading(true);
      try {
        const results: any[] = await actions.listStakingTokenV2({
          limit: 50,
          page: 1,
        });
        const active = (results || []).filter((r: any) =>
          isPoolActiveByEndTime(r?.endTime),
        );
        const mapped = active
          .map(mapStakingTokenToV2)
          .sort((a, b) => Number(b.autoCompound) - Number(a.autoCompound));
        // Enrich each pool with its on-chain total staked (best effort).
        const withTvl = await Promise.all(
          mapped.map(async (p) => ({
            ...p,
            tvl: await readPoolTvl(p.poolContract, p.autoCompound),
          })),
        );
        if (mounted) setV2Pools(withTvl);
      } catch (error) {
        if (mounted) setV2Pools([]);
      } finally {
        if (mounted) setV2PoolsLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Auto-select the first pool once the list is loaded.
  useEffect(() => {
    if (selectedPoolId || v2Pools.length <= 0) {
      return;
    }

    setSelectedPoolId(v2Pools[0].poolId);
  }, [v2Pools, selectedPoolId]);

  const saveLog = async (
    contractAddress: string,
    hash: any,
    amount: any,
    type: number,
  ) => {
    try {
      await actions.addHistory({
        ownerAddress: address,
        contractAddress,
        txHash: hash,
        amount,
        type,
        assetToken: "Mey",
      });
    } catch {
      /* logging is best-effort */
    }
  };

  // ---- Signature 1: unstake the ENTIRE V1 balance ----
  const handleUnstake = async () => {
    try {
      if (selectedNetworkId !== WORK_CHAIN) {
        Noti("error", "Wrong network", 3000);
        return;
      }
      if (!v1Contract) {
        Noti("error", "Stake contract not found", 3000);
        return;
      }
      if (!(Number(stakeBalance) > 0)) {
        Noti("error", "No staked balance to unstake", 3000);
        return;
      }
      setTxLabel("Unstaking from V1…");
      setTxStatus("signing1");
      const res = await v1Contract.unstake(rawStakeBalance);
      // saveLog(poolContract, res?.hash, stakeBalance, 2);
      setTxStatus("pending1");
      const receipt = await res.wait();
      if (receipt?.status === 1) {
        Noti("success", "Unstake successfully", 3000);
        setRestakeAmount(String(stakeBalance));
        setRefresh((x) => x + 1);
        setIsCheckAction?.(!isCheckAction);
        setFlowStep(RESTAKE_STEPS.WITHDRAW);
        setTxStatus("idle");
      } else {
        Noti("error", "Unknown error (status hash fail)", 3000);
        setTxStatus("idle");
      }
    } catch (err: any) {
      setTxStatus("idle");
      Noti(
        "error",
        err?.reason || err?.message || "Transaction rejected",
        3000,
      );
    }
  };

  console.log("selectedV2Pool", selectedV2Pool);

  // ---- Signature 2: stake the withdrawn amount into the selected V2 pool ----
  const executeRestakeToV2 = async () => {
    try {
      if (selectedNetworkId !== WORK_CHAIN) {
        Noti("error", "Wrong network", 3000);
        return;
      }
      if (!selectedV2Pool) {
        Noti("error", "Please select a V2 pool first", 3000);
        return;
      }
      if (!v1Contract || !v2Contract || !v2Token) {
        // Happens while V2 pools are mocked with placeholder addresses.
        Noti("error", "V2 pool contract is not available yet", 3000);
        return;
      }
      const amountWei =
        rawPending && rawPending !== "0"
          ? rawPending
          : web3.utils.toWei(String(restakeAmount || "0"), "ether");
      if (!(BigInt(amountWei) > BigInt(0))) {
        Noti("error", "Nothing to restake", 3000);
        return;
      }

      setTxStatus("signing2");

      // 1. If tokens are still pending in the V1 pool (not withdrawn yet),
      //    withdraw them to the wallet first. If already withdrawn
      //    (inUnStakeAmount == 0), skip straight to approve + stake.
      if (rawPending && BigInt(rawPending) > BigInt(0)) {
        setTxLabel("Withdrawing to wallet…");
        const withdrawTx = await v1Contract.withdraw();
        await withdrawTx.wait();
      }

      // 2. Approve the V2 pool to pull the token if the current allowance is short.
      const allowance = await v2Token.allowance(
        address,
        selectedV2Pool.poolContract,
      );
      if (BigInt(allowance.toString()) < BigInt(amountWei)) {
        setTxLabel("Approving token…");
        const approveTx = await v2Token.approve(
          selectedV2Pool.poolContract,
          ethers.constants.MaxUint256,
        );
        await approveTx.wait();
      }

      // 3. Stake the withdrawn tokens into the selected V2 pool.
      setTxLabel("Staking into V2…");
      const res = await v2Contract.stake(amountWei);
      setTxStatus("pending2");
      const receipt = await res.wait();
      if (receipt?.status === 1) {
        // Restake completed -> record V2 restaking history (type 18)
        // saveLog(selectedV2Pool.poolContract, res?.hash, restakeAmount, 18);
        Noti("success", "Restake to V2 successfully", 3000);
        setIsCheckAction?.(!isCheckAction);
        setFlowStep(RESTAKE_STEPS.COMPLETE);
        setTxStatus("done");
      } else {
        Noti("error", "Unknown error (status hash fail)", 3000);
        setTxStatus("idle");
      }
    } catch (err: any) {
      console.error("err", err);
      setTxStatus("idle");
      Noti(
        "error",
        err?.reason || err?.message || "Transaction rejected",
        3000,
      );
    }
  };

  // User actively cancels/closes the flow after signature 1 but before
  // signature 2 -> spec requires a "Restake unsuccessfully!" toast. The on-chain
  // pending balance (inUnStakeAmount) lets the flow resume on next entry.
  const handleFinish = () => {
    reset();
    onExit?.();
  };

  const goToV2Position = () => {
    window.open(STAKING_PORTFOLIO_URL, "_blank");
  };

  return (
    <div className="rv2-flow">
      {busy && (
        <div className="rv2-loading-overlay">
          <LoadingPage />
          {txLabel && <div className="rv2-loading-label">{txLabel}</div>}
        </div>
      )}

      <div className="rv2-header">
        <div className="rv2-pill">
          <span className="rv2-pill-inner">Restaking V2</span>
        </div>
        <RestakeProgress step={flowStep} />
      </div>

      <div className="rv2-body">
        <div
          className={`rv2-main ${
            flowStep === RESTAKE_STEPS.COMPLETE ? "is-complete" : ""
          }`}
        >
          {/* STEP 0 - Unstake */}
          {flowStep === RESTAKE_STEPS.UNSTAKE && (
            <div className="rv2-step center">
              <div className="rv2-title">Unstake Your Tokens</div>
              <div className="rv2-desc">
                To continue restaking to V2, you must unstake your entire staked
                balance. <br /> Partial unstaking is not supported.
              </div>
              <div className="rv2-balance-box">
                <div className="lb">Current Stake Balance</div>
                <div className="vl">
                  {formatNumber(stakeBalance, NUM_FORMAT)}{" "}
                  <span>${symbol}</span>
                </div>
              </div>
              <div className="rv2-actions col">
                <button
                  className={`rv2-btn primary ${
                    !(Number(stakeBalance) > 0) || busy ? "dis" : ""
                  }`}
                  disabled={!(Number(stakeBalance) > 0) || busy}
                  onClick={handleUnstake}
                >
                  Continue
                </button>
                <div className="rv2-hint">This action cannot be undone.</div>
              </div>
            </div>
          )}

          {/* STEP 2 - Withdraw to restake */}
          {flowStep === RESTAKE_STEPS.WITHDRAW && (
            <div className="rv2-step center rv2-withdraw">
              <div className="rv2-title">Withdraw to restake V2</div>
              <div className="rv2-desc">
                Your withdrawal is ready to be processed. <br /> Once confirmed,
                your tokens will be transferred to your selected V2 staking pool
                and your restaking will be completed.
              </div>
              <div className="rv2-balance-box">
                <div className="lb">You will withdraw</div>
                <div className="vl">
                  {formatNumber(restakeAmount, NUM_FORMAT)}{" "}
                  <span>${symbol}</span>
                </div>
              </div>
              <div className="rv2-selected-pool">
                <div className="rv2-selected-label">Select V2 Pool</div>
                <div className="rv2-pool-list">
                  {v2PoolsLoading ? (
                    <Skeleton active paragraph={{ rows: 6 }} />
                  ) : v2Pools.length === 0 ? (
                    <div className="rv2-empty">No V2 pools available</div>
                  ) : (
                    v2Pools.map((pool) => (
                      <V2PoolCard
                        key={pool.poolId}
                        pool={pool}
                        selected={selectedPoolId === pool.poolId}
                        onSelect={() => setSelectedPoolId(pool.poolId)}
                      />
                    ))
                  )}
                </div>
              </div>
              <div className="rv2-actions col">
                <button
                  className={`rv2-btn primary ${
                    busy || !selectedV2Pool ? "dis" : ""
                  }`}
                  disabled={busy || !selectedV2Pool}
                  onClick={executeRestakeToV2}
                >
                  Confirm
                </button>
                <div className="rv2-hint">This action cannot be undone.</div>
              </div>
            </div>
          )}

          {/* STEP 3 - Complete */}
          {flowStep === RESTAKE_STEPS.COMPLETE && (
            <div className="rv2-complete">
              <div className="rv2-complete-top">
                <div className="rv2-success-icon">
                  <img src="/images/staking/v2-complete-verify.png" alt="" />
                </div>
                <div className="rv2-complete-text">
                  <div className="rv2-title">Restaking to V2 Complete!</div>
                  <div className="rv2-desc">
                    Your tokens have been successfully restaked into your
                    selected V2 staking pool.
                  </div>
                </div>
              </div>
              <div className="rv2-actions col">
                <button
                  className="rv2-btn primary rv2-btn-icon"
                  onClick={goToV2Position}
                >
                  <img src="/images/staking/icon-export.svg" alt="" /> View V2
                  Staking Position
                </button>
                <button className="rv2-btn ghost" onClick={handleFinish}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right-side panel - same Action component the Stake/Unstake tabs use */}
        <Action
          stakeSc={poolContract}
          tokenStake={tokenContract}
          isSuccess={refresh}
          tabBox={"restake"}
          parentCallback={() => setRefresh((x) => x + 1)}
          parentCallback1={() => {}}
          time={endTime}
        />
      </div>
    </div>
  );
};

export default RestakeV2;
